import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { ComplianceCaseStatus, ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { GetProposedCasesForPositionDocument } from "~/graphql/supergraphHooks";
import {
  type GetProposedCasesForPositionQuery,
  type GetProposedCasesForPositionQueryVariables,
} from "~/graphql/supergraphTypes";
import { ProposedCaseFilters, ProposedCaseSort } from "../../types";
import { transformRowData } from "./utils/transformRowData";

type ProposedCaseSliceRow = NonNullable<
  NonNullable<GetProposedCasesForPositionQuery["complianceCasesSlice"]>["rows"]
>[number];

const objectIdOrEmpty = (objectId: string | null | undefined): string =>
  (objectId ?? "").toString().trim();

const isDanglingOnboardingCase = (row: ProposedCaseSliceRow): boolean =>
  !objectIdOrEmpty(row.externalArena?.objectId);

const isUnlinkedEntityCaseForClient = (
  row: ProposedCaseSliceRow,
  clientObjectId: string | undefined,
): boolean => {
  const expectedClientId = objectIdOrEmpty(clientObjectId);
  if (!expectedClientId) {
    return false;
  }
  const belongsToClient = objectIdOrEmpty(row.externalArena?.objectId) === expectedClientId;
  return belongsToClient && !objectIdOrEmpty(row.externalEntity?.objectId);
};

const visibleProposedCaseRows = (
  rows: ProposedCaseSliceRow[],
  caseType: string,
  clientObjectId: string | undefined,
): ProposedCaseSliceRow[] => {
  if (caseType === ComplianceCaseTypeEnum.EntityOnboarding) {
    return rows.filter((row) => isUnlinkedEntityCaseForClient(row, clientObjectId));
  }
  if (caseType === ComplianceCaseTypeEnum.ClientOnboarding) {
    return rows.filter(isDanglingOnboardingCase);
  }
  return rows;
};

type TextFilterInfo = { filterType: "text"; filter: string };
type SetFilterInfo = { filterType: "set"; values: (string | null)[] };
type ColumnFilterInfo = TextFilterInfo | SetFilterInfo;

// The Program column uses colId "programTypes" for its set filter (program type enums),
// but complianceCasesSlice sorts program by the "programName" backend field.
const SORT_FIELD_MAP: Record<string, string> = {
  programTypes: "programName",
};

const mapSortFieldToGraphQLField = (field: string): string => SORT_FIELD_MAP[field] ?? field;

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): ProposedCaseFilters[] => {
  if (!filterModel) {
    return [];
  }
  if ("filterType" in filterModel) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: ProposedCaseFilters[] = [];
  Object.entries(filterModel).forEach(([field, rawFilterInfo]) => {
    const filterInfo = rawFilterInfo as ColumnFilterInfo;
    if (filterInfo.filterType === "set") {
      queryFilters.push({ field, filterValues: filterInfo.values });
    } else if (filterInfo.filterType === "text") {
      queryFilters.push({ field, filterValues: [filterInfo.filter] });
    }
  });
  return queryFilters;
};

export const getSortVars = (sortItems: AgGrid.SortModelItem[]): ProposedCaseSort[] => {
  return (sortItems ?? []).map((sort) => ({
    field: mapSortFieldToGraphQLField(sort.colId),
    sortAscending: sort.sort === "asc",
  }));
};

type ProposedCasesDataSourceOpts = {
  arenaId?: number;
  entityName?: string;
  caseType?: string;
  clientObjectId?: string;
  closedCasesOnly?: boolean;
  positionId?: string;
};

export class ProposedCasesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  opts: Required<Pick<ProposedCasesDataSourceOpts, "caseType" | "closedCasesOnly">> &
    Pick<
      ProposedCasesDataSourceOpts,
      "arenaId" | "entityName" | "clientObjectId" | "positionId"
    >;

  constructor(opts: ProposedCasesDataSourceOpts) {
    this.client = client;
    this.opts = {
      arenaId: opts.arenaId,
      entityName: opts.entityName,
      caseType: opts.caseType ?? "investor_onboarding",
      clientObjectId: opts.clientObjectId,
      closedCasesOnly: opts.closedCasesOnly ?? false,
      positionId: opts.positionId,
    };
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - offset;

    const filters: ProposedCaseFilters[] = [];
    if (this.opts.entityName != null) {
      filters.push({ field: "entity", filterValues: [this.opts.entityName] });
    }
    filters.push({ field: "caseType", filterValues: [this.opts.caseType] });
    if (this.opts.entityName != null) {
      filters.push({ field: "positionId", filterValues: [null] });
    }
    if (this.opts.closedCasesOnly) {
      filters.push({ field: "status", filterValues: [ComplianceCaseStatus.Closed] });
    }
    filters.push(...getFilterVars(params.request.filterModel));

    this.client
      .query<GetProposedCasesForPositionQuery, GetProposedCasesForPositionQueryVariables>({
        query: GetProposedCasesForPositionDocument,
        variables: {
          offset,
          limit,
          filters,
          sorts: getSortVars(params.request.sortModel),
          ...(this.opts.arenaId != null && { arenaId: this.opts.arenaId }),
          ...(this.opts.positionId != null && { positionId: this.opts.positionId }),
        },
        fetchPolicy: "no-cache",
      })
      .then((result) => {
        const slice = result.data.complianceCasesSlice;
        const rows = slice?.rows ?? [];
        const visibleRows = visibleProposedCaseRows(
          rows,
          this.opts.caseType,
          this.opts.clientObjectId,
        );
        const clientFiltered =
          this.opts.caseType === ComplianceCaseTypeEnum.EntityOnboarding ||
          this.opts.caseType === ComplianceCaseTypeEnum.ClientOnboarding;
        params.success({
          rowData: transformRowData(visibleRows),
          rowCount: clientFiltered ? visibleRows.length : (slice?.filteredCount ?? 0),
        });
      })
      .catch(() => params.fail());
  }
}
